import React, { useEffect, useRef, useState } from 'react';
import * as styles from './emailPreviewFrame.module.css';

type Props = {
    html: string;
    title: string;
    device?: 'desktop' | 'mobile';
    height?: number;
};

const EmailPreviewFrame: React.FC<Props> = ({ html, title, device = 'desktop', height = 520 }) => {
    const container = useRef<HTMLDivElement>(null);
    const initializedFrames = useRef(new WeakSet<HTMLIFrameElement>());
    const [availableWidth, setAvailableWidth] = useState<number | null>(null);
    useEffect(() => {
        const element = container.current;
        if (!element) return;
        const measure = () => {
            const width = element.getBoundingClientRect().width;
            if (width > 0) setAvailableWidth(width);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    // Scale the rendered page, not its viewport: responsive email CSS must follow the chosen device.
    const width = device === 'mobile' ? 375 : 720;
    const scale = Math.min(1, (availableWidth ?? width) / width);
    return (
        <div ref={container} className={styles.container}>
            <div className={styles.viewport} style={{ width: width * scale, height }}>
                {/* A srcdoc frame inherits the backoffice CSP, which blocks email CSS. */}
                <iframe
                    key={html}
                    title={title}
                    src="/email-preview.html"
                    onLoad={(event) => {
                        const frame = event.currentTarget;
                        const target = frame.contentWindow;
                        // Links can navigate this frame. Only its initial bootstrap receives HTML.
                        if (!target || initializedFrames.current.has(frame)) return;
                        initializedFrames.current.add(frame);
                        target.postMessage(html, '*');
                    }}
                    sandbox="allow-scripts"
                    referrerPolicy="no-referrer"
                    width={width}
                    height={Math.ceil(height / scale)}
                    className={styles.frame}
                    style={{ transform: `scale(${scale})` }}
                />
            </div>
        </div>
    );
};

export default EmailPreviewFrame;
